import Lenis from "lenis";

import {
  StatsLineChart,
  StatsLineChartDataKey,
  StatsLineItem,
} from "./StatsLineChart";
import { CanvasBackgroundMedia } from "./CanvasBackgroundMedia";
import { LINE_CHART_DATA_ONE, LINE_CHART_DATA_TWO } from "../data.json";
import {
  formatNumberToDecimal,
  getHorizontalTicketBackgroundClipPath,
  getVerticalTicketBackgroundClipPath,
  isMobile,
  isMobileVideoLoadTest,
} from "../utils";

export type PlayerData = {
  id: string;
  firstName: string;
  lastName: string;
  number: string;
  points: number;
  assists: number;
  rebounds: number;
  steals: number;
  blocks: number;
  videoUrl?: string;
  imageUrl: string;
  mobileVideoUrl?: string;
  mobileImageUrl?: string;
};

type LineUpProps = {
  lenis?: Lenis;
  players: PlayerData[];
  dataKeyAbbreviation: Record<StatsLineChartDataKey, string>;
};

const SORT_KEYS = ["points", "assists", "rebounds", "steals", "blocks"];

const PLAYER_DASHBOARD_MARGIN_TOP = 24;

const BASE_DASHBOARD_MODAL_TRANSITION_DURATION = 400;
const BASE_DASHBOARD_MODAL_ROW_TOP = 440;

const LINE_UP_VIDEO_CONTAINER_WRAPPER_TRANSLATE_Y_ANIMATION_DURATION = 300;

const MOBILE_BACKGROUND_ASSETS_MAX_WIDTH = 640;

export class LineUp {
  players: PlayerData[];
  sortedPlayers: PlayerData[] = [];
  sortKey: "points" | "assists" | "rebounds" | "steals" | "blocks" = "points";
  dataKeyAbbreviation: Record<StatsLineChartDataKey, string>;

  lenis?: Lenis;
  dashboardLenis?: Lenis;
  preventDashboardLenisScroll = false;
  forceDashboardLenisScroll = false;
  returnDashboardLenisScrollTimeoutId?: number;

  tableRowTemplate: HTMLTemplateElement;
  playerDashboardTemplate: HTMLTemplateElement;

  pageMain: HTMLElement;
  lineUpSection: HTMLElement;
  tableBody: HTMLDivElement;
  tableColumnHeaders: HTMLSpanElement[] = [];
  sortButtons: HTMLButtonElement[] = [];
  lineUpVideoContainerWrapper: HTMLDivElement;
  lineUpVideoContainer: HTMLDivElement;
  lineUpVideoBackgroundItems: HTMLDivElement;

  lineUpVideoContainerWrapperTranslateY = 0;
  activePlayerDashboardTranslateY = 0;

  playerBackgroundImageData: Record<string, HTMLImageElement> = {};
  mobilePlayerBackgroundImageData: Record<string, HTMLImageElement> = {};
  playerBackgroundVideoData: Record<string, HTMLVideoElement> = {};
  mobilePlayerBackgroundVideoData: Record<string, HTMLVideoElement> = {};

  defaultBackgroundVideosLength = 0;

  useMobileBackgroundAssets =
    window.innerWidth <= MOBILE_BACKGROUND_ASSETS_MAX_WIDTH;

  lineUpVideoCanvas: HTMLCanvasElement;

  canvasBackgroundImage: CanvasBackgroundMedia;
  canvasBackgroundVideo: CanvasBackgroundMedia;

  playerDashboardContentTemplateData: Record<string, HTMLTemplateElement> = {};

  hoveredTableRowPlayerId?: string;

  activePlayerDashboardId?: string;

  updatingTableRows = false;

  updateActivePlayerBackgroundVideoTimeout?: number;
  mobilePlayVideoTimeout?: number;

  otherPageMainSections: HTMLElement[];

  isPlayerDashboardActive = false;
  isPlayerDashboardRendered = false;

  scrollbarWidth = 0;

  bodyResizeObserver: ResizeObserver;
  playerDashboardResizeObserver: ResizeObserver;
  playerDashboardHeaderResizeObserver?: ResizeObserver;
  playerDashboardTicketResizeObserver?: ResizeObserver;

  lineUpVideoContainerWrapperTranslateYAnimationStartAt?: number;

  statsLineChart?: StatsLineChart;

  isMobile = isMobile();
  mobileVideoLoadTest = isMobileVideoLoadTest();

  prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)")
    .matches;

  constructor(props: LineUpProps) {
    this.lenis = props.lenis;

    this.players = props.players;
    this.dataKeyAbbreviation = props.dataKeyAbbreviation;

    this.tableRowTemplate = document.querySelector(
      "#lineup-table-row"
    ) as HTMLTemplateElement;

    this.playerDashboardTemplate = document.querySelector(
      "#player-dashboard"
    ) as HTMLTemplateElement;

    this.lineUpVideoContainerWrapper = document.querySelector(
      "#lineup-video-container-wrapper"
    ) as HTMLDivElement;

    this.lineUpVideoContainer = document.querySelector(
      "#lineup-video-container"
    ) as HTMLDivElement;

    this.lineUpVideoBackgroundItems = document.querySelector(
      "#lineup-video-background-items"
    ) as HTMLDivElement;

    this.tableBody = document.querySelector(
      "#lineup-table-body"
    ) as HTMLTableSectionElement;

    this.pageMain = document.querySelector("#homepage-main") as HTMLElement;

    this.otherPageMainSections = Array.from(
      document.querySelectorAll<HTMLElement>(
        "#homepage-main > section:not([id='lineup']), #homepage-main > footer"
      )
    );

    this.canvasBackgroundImage = new CanvasBackgroundMedia(
      document.querySelector(
        "#lineup-background-image-canvas"
      ) as HTMLCanvasElement
    );

    this.lineUpVideoCanvas = document.querySelector(
      "#lineup-background-video-canvas"
    ) as HTMLCanvasElement;

    this.canvasBackgroundVideo = new CanvasBackgroundMedia(
      this.lineUpVideoCanvas,
      true
    );

    this.lineUpSection = document.querySelector("#lineup") as HTMLElement;

    this.bodyResizeObserver = new ResizeObserver(this.onBodyResize);
    this.bodyResizeObserver.observe(document.body);

    this.playerDashboardResizeObserver = new ResizeObserver(
      this.onPlayerDashboardResize
    );

    this.init();

    window.addEventListener("resize", this.onWindowResize);
  }

  init() {
    this.addPlayersDashboardContentTemplate();

    this.tableColumnHeaders = Array.from(
      document.querySelectorAll(
        "#lineup-table-header [role='columnheader']:not([aria-sort='none'])"
      )
    );

    this.sortButtons = Array.from(
      document.querySelectorAll("#lineup-table-header button")
    );

    this.sortButtons.forEach((button, index) => {
      button.addEventListener("click", () => {
        if (this.updatingTableRows) {
          return;
        }

        if (this.sortKey === SORT_KEYS[index]) {
          return;
        }

        this.sortKey = SORT_KEYS[index] as LineUp["sortKey"];

        button.setAttribute("aria-pressed", "true");

        this.sortPlayers();
        this.updateTableRows();

        if (this.hoveredTableRowPlayerId) {
          this.hoveredTableRowPlayerId = undefined;

          const hoveredTableRowPlayerId = this.sortedPlayers[0].id;

          this.updateActiveHoverTableRow({
            id: hoveredTableRowPlayerId,
            index: 0,
          });
        }
      });
    });

    this.sortPlayers();
    this.renderTableRows();

    this.addPlayersBackgroundVideoElements();

    window.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isPlayerDashboardRendered) {
        e.preventDefault();
        this.closePlayerDashboard();
      }
    });

    window.addEventListener("hashchange", this.onWindowHashChange);

    if (location.hash) {
      const hash = location.hash.replace("#", "");

      const playerIndex = this.sortedPlayers.findIndex(
        (player) => player.id === hash
      );

      if (playerIndex >= 0) {
        const playerTableRow = this.tableBody.children[
          playerIndex
        ] as HTMLDivElement;

        playerTableRow.scrollIntoView({ block: "center" });

        this.updateActiveHoverTableRow({
          id: hash,
          index: playerIndex,
          noDelay: true,
        });

        this.openPlayerDashboard(
          this.tableBody.children[playerIndex] as HTMLDivElement,
          hash,
          true
        );
      }
    }

    document.body.addEventListener("loader-25:loader-exited", () => {
      if (this.isPlayerDashboardActive) {
        document.body.style.setProperty("overflow", "hidden");
      }
    });
  }

  onBodyResize = () => {
    if (!this.isPlayerDashboardActive) {
      this.scrollbarWidth = window.innerWidth - document.body.clientWidth;
    }
  };

  onPlayerDashboardResize = () => {
    if (this.isPlayerDashboardActive) {
      const playerDashboard = document.querySelector(
        ".player-dashboard"
      ) as HTMLElement;

      this.scrollbarWidth =
        playerDashboard.offsetWidth - playerDashboard.clientWidth;

      this.pageMain.style.setProperty(
        "--content-padding-right-offset",
        `${this.scrollbarWidth}px`
      );
    }
  };

  sortPlayers() {
    this.sortedPlayers = [...this.players].sort(
      (a, b) => b[this.sortKey] - a[this.sortKey]
    );

    this.tableColumnHeaders.forEach((header) => {
      header.removeAttribute("aria-sort");
    });

    const sortKeyIndex = SORT_KEYS.indexOf(this.sortKey);

    this.tableColumnHeaders[sortKeyIndex].setAttribute(
      "aria-sort",
      "descending"
    );

    this.sortButtons.forEach((button, index) => {
      if (index !== sortKeyIndex) {
        button.removeAttribute("aria-pressed");
      }
    });

    if (this.isMobile) {
      window.addEventListener("scroll", this.onMobileWindowScroll, {
        passive: true,
      });
    }
  }

  renderTableRows() {
    this.tableBody.innerHTML = "";

    this.sortedPlayers.forEach((player, index) => {
      const tableRowTemplate = this.tableRowTemplate.content.cloneNode(
        true
      ) as HTMLElement;

      const tableRow = tableRowTemplate.children[0] as HTMLDivElement;

      tableRow.setAttribute("data-player-id", player.id);

      tableRow.addEventListener("click", () => this.onPlayerRowClick(tableRow));

      tableRow.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          this.onPlayerRowClick(tableRow);
        }
      });

      tableRow.addEventListener("pointerover", () =>
        this.onPlayerRowPointerOver(tableRow)
      );

      // add move for more stability when scrolling
      tableRow.addEventListener("pointermove", () =>
        this.onPlayerRowPointerOver(tableRow)
      );

      tableRow.addEventListener("focus", () =>
        this.onPlayerRowPointerOver(tableRow)
      );

      const cells = tableRow.querySelectorAll("[role='cell']");

      cells[0].children[0].appendChild(
        document.createTextNode((index + 1).toString())
      );

      if (player.lastName.includes("-")) {
        cells[1].firstChild!.firstChild!.textContent = ` ${player.firstName} ${player.lastName}`;
      } else {
        cells[1].firstChild!.firstChild!.textContent = player.firstName;

        cells[1].firstChild!.firstChild!.appendChild(
          document.createElement("br")
        );
        cells[1].firstChild!.firstChild!.appendChild(
          document.createTextNode(player.lastName)
        );
      }

      cells[1].firstChild!.childNodes[1]!.textContent = `[${player.number}]`;

      cells[2].firstChild!.textContent = formatNumberToDecimal(player.points);
      cells[2]!.classList.toggle(
        "homepage-lineup-table-sorted",
        this.sortKey === "points"
      );
      (cells[2].firstChild as HTMLSpanElement).classList.toggle(
        "homepage-lineup-table-item--sorted",
        this.sortKey === "points"
      );

      cells[3].firstChild!.textContent = formatNumberToDecimal(player.assists);
      cells[3]!.classList.toggle(
        "homepage-lineup-table-sorted",
        this.sortKey === "assists"
      );
      (cells[3].firstChild as HTMLSpanElement).classList.toggle(
        "homepage-lineup-table-item--sorted",
        this.sortKey === "assists"
      );

      cells[4].firstChild!.textContent = formatNumberToDecimal(player.rebounds);
      cells[4]!.classList.toggle(
        "homepage-lineup-table-sorted",
        this.sortKey === "rebounds"
      );
      (cells[4].firstChild as HTMLSpanElement).classList.toggle(
        "homepage-lineup-table-item--sorted",
        this.sortKey === "rebounds"
      );

      cells[5].firstChild!.textContent = formatNumberToDecimal(player.steals);
      cells[5]!.classList.toggle(
        "homepage-lineup-table-sorted",
        this.sortKey === "steals"
      );
      (cells[5].firstChild as HTMLSpanElement).classList.toggle(
        "homepage-lineup-table-item--sorted",
        this.sortKey === "steals"
      );

      cells[6].firstChild!.textContent = formatNumberToDecimal(player.blocks);
      cells[6]!.classList.toggle(
        "homepage-lineup-table-sorted",
        this.sortKey === "blocks"
      );
      (cells[6].firstChild as HTMLSpanElement).classList.toggle(
        "homepage-lineup-table-item--sorted",
        this.sortKey === "blocks"
      );

      this.tableBody.appendChild(tableRow);
    });
  }

  updateTableRows() {
    this.updatingTableRows = true;

    const isCompactTable = window.innerWidth <= 1140;

    this.sortedPlayers.forEach((player, index) => {
      const tableRow = this.tableBody.children[index] as HTMLDivElement;

      tableRow.setAttribute("data-player-id", player.id);

      const cells = tableRow.querySelectorAll("[role='cell']");

      if (isCompactTable) {
        // to ensure the visible cell is animating from the previous sorted stat value
        const currentSortedCellSpan = tableRow.querySelector(
          ".homepage-lineup-table-item--sorted"
        ) as HTMLSpanElement;

        cells[2]!.innerHTML = currentSortedCellSpan.outerHTML;
        cells[3]!.innerHTML = currentSortedCellSpan.outerHTML;
        cells[4]!.innerHTML = currentSortedCellSpan.outerHTML;
        cells[5]!.innerHTML = currentSortedCellSpan.outerHTML;
        cells[6]!.innerHTML = currentSortedCellSpan.outerHTML;
      }

      const nameElement = cells[1].firstChild?.cloneNode(
        true
      ) as HTMLSpanElement;

      if (player.lastName.includes("-")) {
        nameElement.firstChild!.textContent = ` ${player.firstName} ${player.lastName}`;
      } else {
        nameElement.firstChild!.textContent = player.firstName;

        nameElement.firstChild!.appendChild(document.createElement("br"));
        nameElement.firstChild!.appendChild(
          document.createTextNode(player.lastName)
        );
      }

      nameElement.childNodes[1]!.textContent = `[${player.number}]`;

      (cells[1]! as HTMLSpanElement).style.setProperty(
        "max-height",
        `${(cells[1]! as HTMLSpanElement).getBoundingClientRect().height}px`
      );

      cells[1]!.appendChild(nameElement);

      const pointsElement = cells[2].firstChild?.cloneNode(
        true
      ) as HTMLSpanElement;

      pointsElement.textContent = formatNumberToDecimal(player.points);
      pointsElement.classList.toggle(
        "homepage-lineup-table-item--sorted",
        this.sortKey === "points"
      );

      cells[2]!.appendChild(pointsElement);
      cells[2]!.classList.toggle(
        "homepage-lineup-table-sorted",
        this.sortKey === "points"
      );

      const assistsElement = cells[3].firstChild?.cloneNode(
        true
      ) as HTMLSpanElement;

      assistsElement.textContent = formatNumberToDecimal(player.assists);
      assistsElement.classList.toggle(
        "homepage-lineup-table-item--sorted",
        this.sortKey === "assists"
      );

      cells[3]!.classList.toggle(
        "homepage-lineup-table-sorted",
        this.sortKey === "assists"
      );
      cells[3]!.appendChild(assistsElement);

      const reboundsElement = cells[4].firstChild?.cloneNode(
        true
      ) as HTMLSpanElement;

      reboundsElement.textContent = formatNumberToDecimal(player.rebounds);
      reboundsElement.classList.toggle(
        "homepage-lineup-table-item--sorted",
        this.sortKey === "rebounds"
      );

      cells[4]!.classList.toggle(
        "homepage-lineup-table-sorted",
        this.sortKey === "rebounds"
      );
      cells[4]!.appendChild(reboundsElement);

      const stealsElement = cells[5].firstChild?.cloneNode(
        true
      ) as HTMLSpanElement;

      stealsElement.textContent = formatNumberToDecimal(player.steals);
      stealsElement.classList.toggle(
        "homepage-lineup-table-item--sorted",
        this.sortKey === "steals"
      );

      cells[5]!.classList.toggle(
        "homepage-lineup-table-sorted",
        this.sortKey === "steals"
      );
      cells[5]!.appendChild(stealsElement);

      const blocksElement = cells[6].firstChild?.cloneNode(
        true
      ) as HTMLSpanElement;

      blocksElement.textContent = formatNumberToDecimal(player.blocks);
      blocksElement.classList.toggle(
        "homepage-lineup-table-item--sorted",
        this.sortKey === "blocks"
      );

      cells[6]!.classList.toggle(
        "homepage-lineup-table-sorted",
        this.sortKey === "blocks"
      );
      cells[6]!.appendChild(blocksElement);
    });

    requestAnimationFrame(() => {
      const firstNameCell = this.tableBody.children[0]!.querySelector(
        "[role='cell']:not(:first-child)"
      ) as HTMLSpanElement;

      firstNameCell.firstChild!.addEventListener(
        "transitionend",
        this.onTableRowsUpdateComplete,
        { once: true }
      );

      this.tableBody.classList.add("homepage-lineup-table-body--updating");

      (Array.from(this.tableBody.children) as HTMLDivElement[]).forEach(
        (tableRow) => {
          const nameCell = tableRow.querySelector(
            "[role='cell']:not(:first-child)"
          ) as HTMLSpanElement;

          const nameCellHeight = (
            nameCell.children[1] as HTMLSpanElement
          ).getBoundingClientRect().height;

          const rowHeight = Math.max(
            nameCellHeight,
            (nameCell.children[0] as HTMLSpanElement).getBoundingClientRect()
              .height
          );

          nameCell.style.setProperty("max-height", `${nameCellHeight}px`);

          nameCell.style.setProperty(
            "grid-template-rows",
            `${rowHeight}px ${rowHeight}px`
          );
        }
      );
    });
  }

  onTableRowsUpdateComplete = () => {
    (Array.from(this.tableBody.children) as HTMLDivElement[]).forEach(
      (tableRow) => {
        const dataCells = tableRow.querySelectorAll(
          "[role='cell']:not(:first-child)"
        );

        (dataCells[0]! as HTMLSpanElement).style.removeProperty("max-height");
        (dataCells[0]! as HTMLSpanElement).style.removeProperty(
          "grid-template-rows"
        );

        dataCells.forEach((cell) => {
          cell.children[0].remove();
        });
      }
    );

    this.tableBody.classList.remove("homepage-lineup-table-body--updating");

    this.updatingTableRows = false;
  };

  onPlayerRowClick = (row: HTMLDivElement) => {
    if (!this.isPlayerDashboardActive) {
      const playerId = row.getAttribute("data-player-id");

      if (playerId) {
        this.openPlayerDashboard(row, playerId);
      }

      row.blur();
    }
  };

  onPlayerRowPointerOver = (row: HTMLDivElement) => {
    const playerId = row.getAttribute("data-player-id");
    const playerIndex = this.sortedPlayers.findIndex(
      (player) => player.id === playerId
    );

    if (playerId && playerIndex >= 0) {
      this.updateActiveHoverTableRow({ id: playerId, index: playerIndex });
    }
  };

  getRowFade = (index: number, activeIndex: number) => {
    if (index === activeIndex) {
      return { opacity: 1, blur: 0 };
    }

    if (Math.abs(index - activeIndex) === 1) {
      return { opacity: 0.4, blur: 1 };
    }

    if (Math.abs(index - activeIndex) === 2) {
      return { opacity: 0.24, blur: 1.5 };
    }

    return { opacity: 0.12, blur: 2 };
  };

  updateActiveHoverTableRow = ({
    id,
    index,
    noDelay,
    isMobileScrollTriggered,
    forceUpdate,
  }: {
    id: string;
    index: number;
    noDelay?: boolean;
    isMobileScrollTriggered?: boolean;
    forceUpdate?: boolean;
  }) => {
    if (this.hoveredTableRowPlayerId === id && !forceUpdate) {
      return;
    }

    const isForcedUpdate = this.hoveredTableRowPlayerId === id;

    if (this.updateActivePlayerBackgroundVideoTimeout) {
      clearTimeout(this.updateActivePlayerBackgroundVideoTimeout);
    }

    if (this.mobilePlayVideoTimeout) {
      clearTimeout(this.mobilePlayVideoTimeout);
    }

    (Array.from(this.tableBody.children) as HTMLDivElement[]).forEach(
      (row, rowIndex) => {
        row.classList.toggle(
          "homepage-lineup-table-row--hovered",
          rowIndex === index
        );

        const getRowFade = this.getRowFade(rowIndex, index);

        row.style.setProperty("--fade-opacity", getRowFade.opacity.toString());
        row.style.setProperty("--fade-blur", `${getRowFade.blur}px`);
      }
    );

    const mobileDelay = isMobileScrollTriggered ? 800 : 100;
    const delayDuration = this.mobileVideoLoadTest
      ? 0
      : this.isMobile
        ? mobileDelay
        : 100;

    const timeoutDuration =
      noDelay || !this.hoveredTableRowPlayerId ? 0 : delayDuration;

    this.hoveredTableRowPlayerId = id;

    if (this.isMobile) {
      this.lineUpVideoCanvas.style.setProperty("opacity", "0");
    }

    this.updateActivePlayerBackgroundVideoTimeout = setTimeout(() => {
      if (!this.isMobile) {
        this.lineUpVideoCanvas.style.setProperty("opacity", "0");
      }

      const backgroundImageElement = this.useMobileBackgroundAssets
        ? this.mobilePlayerBackgroundImageData[id] ||
          this.playerBackgroundImageData[id]
        : this.playerBackgroundImageData[id];

      this.canvasBackgroundImage.setElement(backgroundImageElement);

      const renderMobileVideo =
        this.useMobileBackgroundAssets &&
        Boolean(this.mobilePlayerBackgroundVideoData[id]);

      const backgroundVideoDataEntries = [
        ...Object.entries(this.playerBackgroundVideoData),
        ...Object.entries(this.mobilePlayerBackgroundVideoData),
      ];

      backgroundVideoDataEntries.forEach(([id, video], index) => {
        const isMobileVideoData = index >= this.defaultBackgroundVideosLength;

        const isPlayerVideo = renderMobileVideo
          ? isMobileVideoData && id === this.hoveredTableRowPlayerId
          : !isMobileVideoData && id === this.hoveredTableRowPlayerId;

        if (video && !isPlayerVideo && !video.paused) {
          video.pause();
          video.currentTime = 0;
        }

        if (video && isPlayerVideo) {
          if (!isForcedUpdate) {
            video.currentTime = 0;
          }

          this.canvasBackgroundVideo.setElement(video);
          this.canvasBackgroundVideo.clear();

          if (this.isMobile && !this.mobileVideoLoadTest) {
            this.mobilePlayVideoTimeout = setTimeout(() => {
              video.play();
              this.lineUpVideoCanvas.style.removeProperty("opacity");
            }, 800);
          } else {
            video.play();
            this.lineUpVideoCanvas.style.removeProperty("opacity");
          }
        }
      });
    }, timeoutDuration);
  };

  openPlayerDashboard = (
    row: HTMLDivElement,
    id: string,
    isPageLoad?: boolean
  ) => {
    if (this.lenis) {
      this.lenis.stop();
    }

    this.isPlayerDashboardActive = true;

    document.body.style.setProperty("overflow", "hidden");

    this.pageMain.style.setProperty(
      "--content-padding-right-offset",
      `${this.scrollbarWidth}px`
    );

    this.otherPageMainSections.forEach((section) => {
      section.classList.add("homepage-section--overlayed");
    });

    const rowTop =
      row.getBoundingClientRect().top - PLAYER_DASHBOARD_MARGIN_TOP;

    const animationDurationMultiplier =
      rowTop < BASE_DASHBOARD_MODAL_ROW_TOP ? 0.15 : 0.3;

    const animationDuration =
      (rowTop - BASE_DASHBOARD_MODAL_ROW_TOP) * animationDurationMultiplier +
      BASE_DASHBOARD_MODAL_TRANSITION_DURATION;

    this.pageMain.style.setProperty(
      "--lineup-player-row-animation-duration",
      `${animationDuration}ms`
    );

    this.pageMain.style.setProperty(
      "--lineup-player-row-offset",
      `${-rowTop}px`
    );

    const postActiveRowTop = Math.max(Math.abs(rowTop), 150);

    this.pageMain.style.setProperty(
      "--lineup-player-post-active-row-offset",
      `${postActiveRowTop}px`
    );

    this.lineUpSection.classList.add("homepage-section--lineup-overlayed");

    row.classList.add("homepage-lineup-table-row--active-overlay-row");
    row.classList.add("homepage-lineup-table-row--active-overlay-transition");

    const playerDashboardTemplate =
      this.playerDashboardTemplate.content.cloneNode(true) as HTMLElement;

    const playerDashboard = playerDashboardTemplate
      .children[0] as HTMLDivElement;

    this.pageMain.appendChild(playerDashboard);

    const closeButton = playerDashboard.querySelector(
      "[data-player-dashboard-close-button]"
    ) as HTMLButtonElement;

    closeButton.addEventListener("click", this.closePlayerDashboard);

    this.playerDashboardResizeObserver.observe(playerDashboard);

    this.renderPlayerDashboard({
      playerDashboard,
      row,
      id,
      isPageLoad,
    });

    this.isPlayerDashboardRendered = true;
  };

  renderPlayerDashboard({
    playerDashboard,
    row,
    id,
    isRenderNext,
    isPageLoad,
  }: {
    playerDashboard: HTMLDivElement;
    row: HTMLDivElement;
    id: string;
    isRenderNext?: boolean;
    isPageLoad?: boolean;
  }) {
    history.scrollRestoration = "manual";

    if (isRenderNext) {
      const hasHistoryModalState = history.state && history.state.modalId;

      history.replaceState(
        { modalId: hasHistoryModalState ? id : undefined },
        "",
        `#${id}`
      );
    } else {
      location.hash = `#${id}`;

      if (!isPageLoad) {
        history.replaceState({ modalId: id }, "", `#${id}`);
      }
    }

    this.lineUpVideoContainerWrapperTranslateY -=
      this.lineUpVideoContainerWrapper.getBoundingClientRect().top;

    this.lineUpVideoContainerWrapper.style.setProperty(
      "transform",
      `translateY(${this.lineUpVideoContainerWrapperTranslateY}px)`
    );

    // iOS Chrome occasionally renders dashboard below top 0 when the browser's URL bar is in compact mode
    const dashboardTop = playerDashboard.getBoundingClientRect().top;

    if (dashboardTop !== 0 && !isRenderNext) {
      this.activePlayerDashboardTranslateY -= dashboardTop;

      playerDashboard.style.setProperty(
        "transform",
        `translateY(${this.activePlayerDashboardTranslateY}px)`
      );
    }

    this.activePlayerDashboardId = id;

    const currentPlayerIndex = this.sortedPlayers.indexOf(
      this.players.find((player) => player.id === id)!
    );

    const rowCells = row.querySelectorAll("[role='cell']");

    const headers = Array.from(
      playerDashboard.querySelectorAll("[data-player-dashboard-header]")
    ) as HTMLElement[];

    // second to last header is header for current player
    // last header is header for next player

    headers.at(-2)!.classList.remove("player-dashboard__header--next-player");
    headers.at(-1)!.classList.add("player-dashboard__header--next-player");

    const headerNameEls = Array.from(headers).map((header) =>
      header.querySelector("[data-player-dashboard-header-name]")
    ) as HTMLHeadingElement[];

    if (!isRenderNext) {
      headerNameEls.at(-2)!.innerHTML = rowCells[1].innerHTML;
    }

    const nextPlayerIndex =
      (currentPlayerIndex + 1) % this.sortedPlayers.length;

    const nextPlayerTableRow = this.tableBody.children[nextPlayerIndex];

    const nextPlayerRowCells =
      nextPlayerTableRow.querySelectorAll("[role='cell']");

    headerNameEls.at(-1)!.innerHTML = nextPlayerRowCells[1].innerHTML;

    headerNameEls.at(-1)!.setAttribute("tabindex", "0");

    headerNameEls
      .at(-1)!
      .addEventListener(
        "click",
        () => this.renderNextPlayerDashboard(playerDashboard, nextPlayerIndex),
        { once: true }
      );

    let playerDashboardHeaderHeight = headers.at(-2)!.offsetHeight;

    this.pageMain.style.setProperty(
      "--dashboard-video-title-top-offset",
      `${playerDashboardHeaderHeight}px`
    );

    this.playerDashboardHeaderResizeObserver = new ResizeObserver(() => {
      if (playerDashboardHeaderHeight === headers.at(-2)!.offsetHeight) {
        return;
      }

      playerDashboardHeaderHeight = headers.at(-2)!.offsetHeight;

      this.lineUpVideoContainer.style.setProperty("transition", "none");

      this.pageMain.style.setProperty(
        "--dashboard-video-title-top-offset",
        `${headers.at(-2)!.offsetHeight}px`
      );

      requestAnimationFrame(() => {
        this.lineUpVideoContainer.style.removeProperty("transition");
      });
    });

    this.playerDashboardHeaderResizeObserver.observe(headers.at(-2)!);

    const playerDashboardContent = playerDashboard.querySelector(
      "#player-dashboard-content"
    ) as HTMLDivElement;

    let playerDashboardBody: HTMLDivElement | undefined;

    if (this.playerDashboardContentTemplateData[id]) {
      const playerDashboardGameTicket = (
        this.playerDashboardContentTemplateData[id].content.cloneNode(
          true
        ) as HTMLElement
      ).children[0] as HTMLDivElement;

      playerDashboardBody = (
        this.playerDashboardContentTemplateData[id].content.cloneNode(
          true
        ) as HTMLElement
      ).children[1] as HTMLDivElement;

      playerDashboardContent.insertBefore(playerDashboardBody, headers.at(-1)!);
      playerDashboardContent.insertBefore(
        playerDashboardGameTicket,
        playerDashboardBody
      );

      const ticketElement = playerDashboardGameTicket.children[0];

      if (ticketElement) {
        this.setTicketClipPath(
          playerDashboardGameTicket.children[0] as HTMLDivElement
        );

        this.playerDashboardTicketResizeObserver = new ResizeObserver(() => {
          this.setTicketClipPath(
            playerDashboardGameTicket.children[0] as HTMLDivElement
          );
        });

        this.playerDashboardTicketResizeObserver.observe(
          playerDashboardGameTicket
        );
      } else {
        this.playerDashboardTicketResizeObserver?.disconnect();
      }
    }

    if (!playerDashboardBody) {
      this.closePlayerDashboard();

      return;
    }

    const statsFilterButtons = Array.from(
      playerDashboardBody.querySelectorAll(
        "#player-dashboard-stats-perf-filters button"
      )
    ) as HTMLButtonElement[];

    statsFilterButtons.forEach((button, index) => {
      button.addEventListener("click", () => {
        button.classList.add(
          "player-dashboard-player-stats-perf-button--active"
        );

        statsFilterButtons.forEach((btn, btnIndex) => {
          if (btnIndex !== index) {
            btn.classList.remove(
              "player-dashboard-player-stats-perf-button--active"
            );
          }
        });

        const dataKey = SORT_KEYS[index] as StatsLineChartDataKey;

        this.statsLineChart?.setDataKey(dataKey);
      });
    });

    const filterSelects =
      playerDashboardBody.querySelectorAll<HTMLSelectElement>(
        ".player-dashboard-player-stats-filter select"
      );

    const getSelectedValues = () => {
      const values: { player: string | null; filters: Record<string, string> } =
        {
          player: null,
          filters: {},
        };

      filterSelects.forEach((select) => {
        const playerId = select.dataset.player || null;

        if (!values.player && playerId) {
          values.player = playerId;
        }

        values.filters[select.id] = select.value;
      });

      return values;
    };

    const handleChartResponse = (data: any) => {
      console.log("Fetched data:", data);

      const [
        pointsButton,
        assistsButton,
        reboundsButton,
        stealsButton,
        blocksButton,
      ] = statsFilterButtons;

      const pointsAverage = this.getAverageFromLineChartData(data, "points");
      const assistsAverage = this.getAverageFromLineChartData(data, "assists");
      const reboundsAverage = this.getAverageFromLineChartData(
        data,
        "rebounds"
      );
      const stealsAverage = this.getAverageFromLineChartData(data, "steals");
      const blocksAverage = this.getAverageFromLineChartData(data, "blocks");

      this.updateStatsFilterButtonValue(pointsButton, pointsAverage);
      this.updateStatsFilterButtonValue(assistsButton, assistsAverage);
      this.updateStatsFilterButtonValue(reboundsButton, reboundsAverage);
      this.updateStatsFilterButtonValue(stealsButton, stealsAverage);
      this.updateStatsFilterButtonValue(blocksButton, blocksAverage);

      this.statsLineChart?.setData(data);
    };

    filterSelects.forEach((select) => {
      this.resizeSelectToFitContent(select);

      select.addEventListener("change", () => {
        const selectedValues = getSelectedValues();

        this.resizeSelectToFitContent(select);

        console.log("Selected values:", selectedValues);

        this.statsLineChart?.setData([]);

        fetch(
          `${location.origin}/wp-content/themes/nba-represent/_inc/hp-player-stats.php`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(selectedValues),
          }
        )
          .then((res) => {
            if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
            return res.json();
          })
          .then(handleChartResponse)
          .catch((err) => {
            console.error("Fetch error:", err);

            // if (location.host === "localhost:1234") {
            //   setTimeout(() => {
            //     const [
            //       pointsButton,
            //       assistsButton,
            //       reboundsButton,
            //       stealsButton,
            //       blocksButton,
            //     ] = statsFilterButtons;

            //     const pointsAverage = this.getAverageFromLineChartData(
            //       LINE_CHART_DATA_TWO,
            //       "points"
            //     );
            //     const assistsAverage = this.getAverageFromLineChartData(
            //       LINE_CHART_DATA_TWO,
            //       "assists"
            //     );
            //     const reboundsAverage = this.getAverageFromLineChartData(
            //       LINE_CHART_DATA_TWO,
            //       "rebounds"
            //     );
            //     const stealsAverage = this.getAverageFromLineChartData(
            //       LINE_CHART_DATA_TWO,
            //       "steals"
            //     );
            //     const blocksAverage = this.getAverageFromLineChartData(
            //       LINE_CHART_DATA_TWO,
            //       "blocks"
            //     );

            //     this.updateStatsFilterButtonValue(pointsButton, pointsAverage);
            //     this.updateStatsFilterButtonValue(
            //       assistsButton,
            //       assistsAverage
            //     );
            //     this.updateStatsFilterButtonValue(
            //       reboundsButton,
            //       reboundsAverage
            //     );
            //     this.updateStatsFilterButtonValue(stealsButton, stealsAverage);
            //     this.updateStatsFilterButtonValue(blocksButton, blocksAverage);

            //     this.statsLineChart?.setData(LINE_CHART_DATA_TWO);
            //   }, 1000);
            // }
          });
      });
    });

    /*
    const statsPeriodSelect = playerDashboardBody.querySelector(
      `#stats-period-select-${id}`
    ) as HTMLSelectElement;

    statsPeriodSelect?.addEventListener("change", () => {
      const selectedValue = statsPeriodSelect.value;

      // for demo purposes only, should be replaced with actual player data
      const data =
        selectedValue === "2024-2025"
          ? LINE_CHART_DATA_TWO
          : LINE_CHART_DATA_ONE;

      const dataKeyIndex = statsFilterButtons.findIndex((button) =>
        button.classList.contains(
          "player-dashboard-player-stats-perf-button--active"
        )
      );

      this.statsLineChart?.destroy();

      this.statsLineChart = new StatsLineChart({
        container: statsLineChartContainer,
        data,
        dataKey: SORT_KEYS[dataKeyIndex] as StatsLineChartDataKey,
        dataKeyAbbreviation: this.dataKeyAbbreviation,
        onChartContainerScroll: this.onStatsChartContainerScroll,
      });
    });

       */

    if (this.lenis) {
      this.dashboardLenis = new Lenis({
        wrapper: playerDashboard,
        content: playerDashboardContent,
        autoRaf: true,
        prevent: () =>
          this.preventDashboardLenisScroll && !this.forceDashboardLenisScroll,
      });
    }

    const statsLineChartContainer = playerDashboardBody.querySelector(
      "[data-player-stats-chart]"
    ) as HTMLDivElement;

    this.statsLineChart?.destroy();

    if (statsLineChartContainer) {
      this.statsLineChart = new StatsLineChart({
        container: statsLineChartContainer,
        data: [],
        dataKeyAbbreviation: this.dataKeyAbbreviation,
        onChartContainerScroll: this.onStatsChartContainerScroll,
      });

      const handleChartResponseInitial = (data: any) => {
        console.log("Fetched data:", data);

        this.statsLineChart?.setData(data);

        // this.statsLineChart = new StatsLineChart({
        //   container: statsLineChartContainer,
        //   // should be replaced with actual player data
        //   data,
        //   dataKeyAbbreviation: this.dataKeyAbbreviation,
        //   onChartContainerScroll: this.onStatsChartContainerScroll,
        // });
      };

      fetch(
        `${location.origin}/wp-content/themes/nba-represent/_inc/hp-player-stats-initial.php`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ playerId: this.activePlayerDashboardId }),
        }
      )
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
          return res.json();
        })
        .then(handleChartResponseInitial)
        .catch((err) => {
          console.error("Fetch error:", err);

          // if (location.host === "localhost:1234") {
          //   setTimeout(() => {
          //     this.statsLineChart?.setData(LINE_CHART_DATA_ONE);
          //   }, 0);
          // }
        });
    }

    requestAnimationFrame(() => {
      playerDashboard.classList.add("player-dashboard--active");
    });
  }

  renderNextPlayerDashboard = (
    playerDashboard: HTMLDivElement,
    nextPlayerIndex: number
  ) => {
    const playerData = this.sortedPlayers[nextPlayerIndex];

    const playerTableRow = this.tableBody.children[
      nextPlayerIndex
    ] as HTMLDivElement;

    const playerDashboardContent = playerDashboard.querySelector(
      "#player-dashboard-content"
    ) as HTMLDivElement;

    const [_, header, playerTicket, currentBody, nextHeader] =
      playerDashboardContent.children;

    this.playerDashboardHeaderResizeObserver?.disconnect();

    (header as HTMLElement).style.setProperty("transition", "none");
    (header as HTMLElement).style.setProperty("opacity", "0");

    (playerTicket as HTMLElement).style.setProperty("transition", "none");
    (playerTicket as HTMLElement).style.setProperty("opacity", "0");

    const nextPlayerHeaderNameEl = playerDashboard.querySelector(
      "[data-player-dashboard-header-name][tabindex]"
    ) as HTMLHeadingElement;

    nextPlayerHeaderNameEl.removeAttribute("tabindex");

    playerDashboardContent.appendChild(nextHeader.cloneNode(true));

    this.updateActiveHoverTableRow({
      id: playerData.id,
      index: nextPlayerIndex,
      noDelay: true,
    });

    const currentOverlayRow = this.tableBody.querySelector(
      ".homepage-lineup-table-row--active-overlay-row"
    ) as HTMLDivElement;

    currentOverlayRow.style.setProperty("transition", "none");

    currentOverlayRow.classList.remove(
      "homepage-lineup-table-row--active-overlay-row"
    );

    currentOverlayRow.classList.remove(
      "homepage-lineup-table-row--active-overlay-transition"
    );

    playerTableRow.style.setProperty("transition", "none");

    const playerRowCells = playerTableRow.querySelectorAll(
      "[role='cell']"
    ) as NodeListOf<HTMLSpanElement>;

    playerRowCells.forEach((cell) => {
      cell.style.setProperty("transition", "none");
    });

    playerTableRow.classList.add(
      "homepage-lineup-table-row--active-overlay-row"
    );

    playerTableRow.classList.add(
      "homepage-lineup-table-row--active-overlay-transition"
    );

    const rowTop =
      playerTableRow.getBoundingClientRect().top - PLAYER_DASHBOARD_MARGIN_TOP;

    const animationDurationMultiplier =
      rowTop < BASE_DASHBOARD_MODAL_ROW_TOP ? 0.15 : 0.3;

    const animationDuration =
      (rowTop - BASE_DASHBOARD_MODAL_ROW_TOP) * animationDurationMultiplier +
      BASE_DASHBOARD_MODAL_TRANSITION_DURATION;

    this.pageMain.style.setProperty(
      "--lineup-player-row-animation-duration",
      `${animationDuration}ms`
    );

    // update window scroll to align background table row with header title
    // This also ensures that the dashboard header transforms to the player row position when closed

    window.scrollTo({
      top: rowTop + window.scrollY,
    });

    this.renderPlayerDashboard({
      playerDashboard,
      row: playerTableRow,
      id: playerData.id,
      isRenderNext: true,
    });

    currentOverlayRow.style.removeProperty("transition");
    playerTableRow.style.removeProperty("transition");
    playerRowCells.forEach((cell) => {
      cell.style.removeProperty("transition");
    });

    this.lineUpVideoContainerWrapper.style.setProperty("transition", "none");

    // reset lineup video container wrapper translate Y to fix iOS offset bug
    this.lineUpVideoContainerWrapperTranslateYAnimationStartAt =
      performance.now();

    requestAnimationFrame(this.animateLineUpVideoContainerWrapperTranslateY);

    this.forceDashboardLenisScroll = true;

    if (!this.lenis) {
      // temporary lenis instance to efficiently remove previous dashboard content and scroll to top
      this.dashboardLenis = new Lenis({
        wrapper: playerDashboard,
        content: playerDashboardContent,
        autoRaf: true,
      });
    }

    this.dashboardLenis?.scrollTo(nextPlayerHeaderNameEl.parentElement!, {
      offset: 0,
      lock: true,
      immediate: this.prefersReducedMotion,
      duration: 0.8,
      // ease out cubic
      easing: (t: number) => 1 - Math.pow(1 - t, 3),
      onComplete: () => {
        header.remove();
        playerTicket.remove();
        currentBody.remove();
        playerDashboard.scrollTop = 0;

        this.forceDashboardLenisScroll = false;

        if (!this.lenis) {
          // remove after scroll to top if no main lenis instance
          this.dashboardLenis?.destroy();
          this.dashboardLenis = undefined;
        }

        this.lineUpVideoContainerWrapper.style.removeProperty("transition");

        // iOS Chrome occasionally renders dashboard below top 0 when the browser's URL bar is in compact mode
        const dashboardTop = playerDashboard.getBoundingClientRect().top;

        if (dashboardTop !== 0) {
          this.activePlayerDashboardTranslateY -= dashboardTop;

          playerDashboard.style.setProperty(
            "transform",
            `translateY(${this.activePlayerDashboardTranslateY}px)`
          );
        }
      },
    });
  };

  animateLineUpVideoContainerWrapperTranslateY = (timestamp: number) => {
    if (
      !this.lineUpVideoContainerWrapperTranslateYAnimationStartAt ||
      timestamp - this.lineUpVideoContainerWrapperTranslateYAnimationStartAt >
        LINE_UP_VIDEO_CONTAINER_WRAPPER_TRANSLATE_Y_ANIMATION_DURATION
    ) {
      return;
    }

    this.lineUpVideoContainerWrapperTranslateY -=
      this.lineUpVideoContainerWrapper.getBoundingClientRect().top;

    this.lineUpVideoContainerWrapper.style.setProperty(
      "transform",
      `translateY(${this.lineUpVideoContainerWrapperTranslateY}px)`
    );

    requestAnimationFrame(this.animateLineUpVideoContainerWrapperTranslateY);
  };

  closePlayerDashboard = () => {
    if (location.hash) {
      if (history.state && history.state.modalId) {
        history.go(-1);
      } else {
        history.pushState(null, "", location.pathname);
      }
    }

    const playerDashboard = document.querySelector(
      ".player-dashboard"
    ) as HTMLElement;

    this.playerDashboardResizeObserver.unobserve(playerDashboard);

    this.isPlayerDashboardActive = false;
    this.isPlayerDashboardRendered = false;

    this.dashboardLenis?.stop();

    this.lineUpVideoContainerWrapperTranslateY = 0;
    this.activePlayerDashboardTranslateY = 0;

    playerDashboard.style.removeProperty("transform");

    this.lineUpVideoContainerWrapper.style.setProperty(
      "transform",
      "translateY(0px)"
    );

    this.otherPageMainSections.forEach((section) => {
      section.classList.remove("homepage-section--overlayed");
    });

    this.lineUpSection.classList.remove("homepage-section--lineup-overlayed");

    const activeRow = this.tableBody.querySelector(
      ".homepage-lineup-table-row--active-overlay-row"
    ) as HTMLDivElement;

    activeRow?.classList.remove(
      "homepage-lineup-table-row--active-overlay-row"
    );

    const closeButton = playerDashboard.querySelector(
      "[data-player-dashboard-close-button]"
    ) as HTMLButtonElement;

    closeButton.addEventListener(
      "transitionend",
      () => {
        const activeRowCells = activeRow.querySelectorAll(
          "[role='cell']"
        ) as NodeListOf<HTMLSpanElement>;

        activeRowCells[0].style.setProperty("transition", "none");
        activeRowCells[1].style.setProperty("transition", "none");

        setTimeout(() => {
          activeRowCells[0].style.removeProperty("transition");
          activeRowCells[1].style.removeProperty("transition");
        }, 100);

        activeRow.classList.remove(
          "homepage-lineup-table-row--active-overlay-transition"
        );

        playerDashboard.remove();

        if (this.lenis) {
          this.lenis.start();

          this.dashboardLenis?.destroy();
          this.dashboardLenis = undefined;
        }

        document.body.style.removeProperty("overflow");
        this.pageMain.style.removeProperty("--content-padding-right-offset");

        history.scrollRestoration = "auto";
      },
      { once: true }
    );

    playerDashboard.classList.remove("player-dashboard--active");
  };

  onWindowHashChange = (e: HashChangeEvent) => {
    const oldHash = new URL(e.oldURL).hash.replace("#", "");
    const newHash = new URL(e.newURL).hash.replace("#", "");

    const newHashPlayerIndex = this.sortedPlayers.findIndex(
      (player) => player.id === newHash
    );

    if (
      this.isPlayerDashboardActive &&
      newHashPlayerIndex < 0 &&
      oldHash === this.activePlayerDashboardId
    ) {
      this.closePlayerDashboard();
    } else if (!this.isPlayerDashboardActive && newHash) {
      if (newHashPlayerIndex === -1) {
        return;
      }

      this.openPlayerDashboard(
        this.tableBody.children[newHashPlayerIndex] as HTMLDivElement,
        newHash
      );
    }
  };

  onMobileWindowScroll = () => {
    if (this.isPlayerDashboardActive) {
      return;
    }

    const activeRowIndex = Array.from(this.tableBody.children).findLastIndex(
      (child) => {
        const { top, height } = child.getBoundingClientRect();
        return window.innerHeight / 2 > top + height / 2;
      }
    );

    if (activeRowIndex !== -1) {
      const activePlayerId = this.sortedPlayers[activeRowIndex].id;

      this.updateActiveHoverTableRow({
        id: activePlayerId,
        index: activeRowIndex,
        isMobileScrollTriggered: true,
      });
    }
  };

  onStatsChartContainerScroll = () => {
    if (this.dashboardLenis) {
      this.preventDashboardLenisScroll = true;

      clearTimeout(this.returnDashboardLenisScrollTimeoutId);

      this.returnDashboardLenisScrollTimeoutId = setTimeout(() => {
        this.preventDashboardLenisScroll = false;
      }, 100);
    }
  };

  onWindowResize = () => {
    const useMobileBackgroundAssets =
      window.innerWidth <= MOBILE_BACKGROUND_ASSETS_MAX_WIDTH;

    if (useMobileBackgroundAssets !== this.useMobileBackgroundAssets) {
      this.useMobileBackgroundAssets = useMobileBackgroundAssets;

      if (this.hoveredTableRowPlayerId) {
        this.updateActiveHoverTableRow({
          id: this.hoveredTableRowPlayerId,
          index: this.sortedPlayers.findIndex(
            (player) => player.id === this.hoveredTableRowPlayerId
          ),
          noDelay: true,
          forceUpdate: true,
        });
      }
    }
  };

  resizeSelectToFitContent = (select: HTMLSelectElement) => {
    const tempOptionContainer = document.createElement("span");
    tempOptionContainer.textContent =
      select.options[select.selectedIndex].textContent;

    tempOptionContainer.style.visibility = "hidden";
    tempOptionContainer.style.whiteSpace = "pre";
    tempOptionContainer.style.position = "absolute";

    const { font, letterSpacing, textTransform } = getComputedStyle(select);

    tempOptionContainer.style.font = font;
    tempOptionContainer.style.letterSpacing = letterSpacing;
    tempOptionContainer.style.textTransform = textTransform;

    document.body.appendChild(tempOptionContainer);

    const selectWidth = tempOptionContainer.offsetWidth + 32;

    select.style.setProperty("width", `${selectWidth}px`);

    document.body.removeChild(tempOptionContainer);
  };

  getAverageFromLineChartData = (
    data: StatsLineItem[],
    dataKey: StatsLineChartDataKey
  ) => {
    const totalGamesWithData = data.filter((entry) =>
      Boolean(entry.gameData)
    ).length;

    if (!totalGamesWithData) {
      return "0.0";
    }

    const total = data.reduce(
      (sum, entry) => sum + Number(entry.gameData?.[dataKey] || 0),
      0
    );

    const average = total / totalGamesWithData;

    return average.toFixed(1);
  };

  updateStatsFilterButtonValue = (button: HTMLButtonElement, value: string) => {
    const buttonValueElement = button.querySelector(
      "[stats-perf-button-value]"
    ) as HTMLSpanElement;

    const prevValueTextElement = Array.from(buttonValueElement.children).at(
      -1
    ) as HTMLSpanElement;
    const nextValueTextElement = prevValueTextElement.cloneNode(
      true
    ) as HTMLSpanElement;

    nextValueTextElement.textContent = value;

    buttonValueElement.innerHTML = "";

    buttonValueElement.appendChild(prevValueTextElement);
    buttonValueElement.appendChild(nextValueTextElement);

    prevValueTextElement.addEventListener("transitionend", () => {
      prevValueTextElement.remove();
    });

    buttonValueElement.classList.add(
      "player-dashboard-player-stats-perf-button__value--updating"
    );

    requestAnimationFrame(() => {
      buttonValueElement.classList.remove(
        "player-dashboard-player-stats-perf-button__value--updating"
      );
    });
  };

  setTicketClipPath = (ticketElement: HTMLDivElement) => {
    const { width: ticketWidth, height: ticketHeight } =
      ticketElement.getBoundingClientRect();

    const { width: columnOneWidth, height: columnOneHeight } = ticketElement
      .querySelectorAll("[data-ticket-column]")[0]
      .getBoundingClientRect();
    const { width: columnTwoWidth, height: columnTwoHeight } = ticketElement
      .querySelectorAll("[data-ticket-column]")[1]
      .getBoundingClientRect();

    const clipPath =
      window.innerWidth > 900
        ? getHorizontalTicketBackgroundClipPath({
            height: ticketHeight,
            columnOneWidth: columnOneWidth,
            columnTwoWidth: columnTwoWidth,
            gap: 10,
            gapInset: 10,
            radius: 2,
          })
        : getVerticalTicketBackgroundClipPath({
            width: ticketWidth,
            columnOneHeight: columnOneHeight,
            columnTwoHeight: columnTwoHeight,
            gap: 10,
            gapInset: 9,
            radius: 2,
          });

    ticketElement.style.setProperty(
      "--ticket-clip-path",
      `path("${clipPath}")`
    );
  };

  addPlayersDashboardContentTemplate = () => {
    this.players.forEach((player) => {
      const playerDashboardContentTemplate = document.querySelector(
        `#player-dashboard-content-${player.id}`
      ) as HTMLTemplateElement;

      if (playerDashboardContentTemplate) {
        this.playerDashboardContentTemplateData[player.id] =
          playerDashboardContentTemplate;
      } else {
        console.log(`NO PLAYER DASHBOARD CONTENT TEMPLATE FOR: ${player.id}`);
      }
    });

    this.players = this.players.filter(
      (player) => this.playerDashboardContentTemplateData[player.id]
    );
  };

  addPlayersBackgroundVideoElements = () => {
    let defaultBackgroundVideosLength = 0;

    this.players.forEach((player) => {
      const imageElement = document.createElement("img");
      imageElement.src = player.imageUrl;

      this.playerBackgroundImageData[player.id] = imageElement;

      if (player.mobileImageUrl) {
        const mobileImageElement = document.createElement("img");
        mobileImageElement.src = player.mobileImageUrl;

        this.mobilePlayerBackgroundImageData[player.id] = mobileImageElement;
      }

      if (player.videoUrl) {
        const videoElement = document.createElement("video");

        videoElement.src = player.videoUrl;
        videoElement.poster = player.imageUrl;
        videoElement.muted = true;
        videoElement.loop = true;
        videoElement.playsInline = true;
        videoElement.preload = this.mobileVideoLoadTest ? "auto" : "none";

        if (this.mobileVideoLoadTest) {
          videoElement.load();
        }

        this.playerBackgroundVideoData[player.id] = videoElement;

        defaultBackgroundVideosLength++;
      }

      if (player.mobileVideoUrl) {
        const mobileVideoElement = document.createElement("video");

        mobileVideoElement.src = player.mobileVideoUrl;
        mobileVideoElement.poster = player.mobileImageUrl || player.imageUrl;
        mobileVideoElement.muted = true;
        mobileVideoElement.loop = true;
        mobileVideoElement.playsInline = true;
        mobileVideoElement.preload = this.mobileVideoLoadTest ? "auto" : "none";

        if (this.mobileVideoLoadTest) {
          mobileVideoElement.load();
        }

        this.mobilePlayerBackgroundVideoData[player.id] = mobileVideoElement;
      }
    });

    this.defaultBackgroundVideosLength = defaultBackgroundVideosLength;
  };
}
